/**
 * RashiChakraView — IChartView adapter for Rashi Chakra (South Indian grid)
 *
 * Encapsulates: lazy dynamic import + data preparation + RashiChakraRenderer lifecycle
 */

import * as d3 from 'd3';
import type { IChartView } from '../ChartViewManager';
import type { ChartViewMode } from '../../../../../../types/ChartViewTypes';
import type { RashiChakraRenderer as RashiChakraRendererType } from '../../modules/RashiChakraRenderer';
import { chakraDataService } from '../../../../../../services/ChakraDataService';
import { devWarn } from '../../../../../../utils/devLog';

export class RashiChakraView implements IChartView {
    readonly viewId: ChartViewMode = 'rashiChakra';

    private chartId: string;
    private renderer: RashiChakraRendererType | null = null;
    private rendered: boolean = false;

    constructor(chartId: string) {
        this.chartId = chartId;
    }

    async activate(_options?: Record<string, any>): Promise<void> {
        const data = chakraDataService.prepareRashiChakraData(this.chartId);
        if (!data) {
            devWarn(`RashiChakraView[${this.chartId}]: No rashi chakra data available`);
            return;
        }

        const container = this.getContainer();
        if (!container) return;

        if (!this.renderer) {
            const { RashiChakraRenderer } = await import('../../modules/RashiChakraRenderer');
            this.renderer = new RashiChakraRenderer(this.chartId, container);
        }
        this.renderer.render(data);
        this.renderer.show();
        this.rendered = true;
    }

    deactivate(): void {
        this.renderer?.hide();
    }

    destroy(): void {
        if (this.renderer) {
            const container = this.getContainer();
            container?.selectAll('*').remove();
        }
        this.renderer = null;
        this.rendered = false;
    }

    isRendered(): boolean {
        return this.rendered;
    }

    private getContainer(): d3.Selection<any, unknown, null, undefined> | null {
        const sel = d3.select(`#rashi-chakra-container-${this.chartId}`);
        return sel.empty() ? null : sel as unknown as d3.Selection<any, unknown, null, undefined>;
    }
}
